import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card } from '@/design-system/ui/Card';
import { Button } from '@/design-system/ui/Button';
import { Input } from '@/design-system/ui/Field';
import { Icon } from '@/design-system/ui/Icon';
import { Sheet } from '@/design-system/ui/Sheet';
import { EmptyState } from '@/design-system/ui/States';
import { toApiError } from '@/core/api/errors';
import { toEnglishDigits } from '@/core/format/numbers';
import { isValidMobile } from '@/core/validation/validators';
import { useSEO } from '@/core/seo/useSEO';
import { submitContactRequest } from '@/features/support/api/support-api';
import { PageHeader } from '../components/PageHeader';
import { openPositions, type Position } from '../data/careers';

const EMPTY = { name: '', mobile: '', resumeLink: '' };

export default function CareersPage() {
  useSEO({
    title: 'فرصت‌های شغلی و استخدام',
    description: 'فرصت‌های شغلی فعال و استخدام در بلینو؛ پیوستن به تیم توسعه فناوری، پشتیبانی و عملیات حمل‌ونقل بین‌شهری.',
    canonical: '/careers',
    keywords: 'استخدام بلینو, فرصت های شغلی بلینو, استخدام در کرمان, استخدام برنامه نویس کرمان',
  });

  const [active, setActive] = useState<Position | null>(null);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <PageHeader icon="briefcase" title="همکاری با ما" subtitle="به تیم فناوری حمل‌ونقل و لجستیک بین‌شهری بلینو بپیوندید" />

      {openPositions.length === 0 ? (
        <Card>
          <EmptyState
            title="در حال حاضر فرصت شغلی فعالی نداریم"
            description="به محض ایجاد نیاز به نیروی جدید، فرصت‌های شغلی همین‌جا اعلام خواهد شد. لطفاً بعداً دوباره سر بزنید یا از طریق صفحهٔ تماس با پشتیبانی با ما در ارتباط باشید."
            action={<Link to="/contact"><Button variant="outline" size="sm">تماس با پشتیبانی</Button></Link>}
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {openPositions.map((p) => (
            <li key={p.id}>
              <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <div>
                  <h2 className="font-bold">{p.title}</h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-2">
                    <span className="inline-flex items-center gap-1"><Icon name="building" size={15} />{p.department}</span>
                    <span className="inline-flex items-center gap-1"><Icon name="pin" size={15} />{p.location}</span>
                    <Badge>{p.type}</Badge>
                  </div>
                </div>
                <Button size="sm" onClick={() => setActive(p)}>ارسال رزومه</Button>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {active && <ApplySheet key={active.id} position={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function ApplySheet({ position, onClose }: { position: Position; onClose: () => void }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState<{ name?: string; mobile?: string }>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.name.trim()) next.name = 'لطفاً نام خود را وارد کنید';
    if (!isValidMobile(form.mobile)) next.mobile = 'شماره موبایل معتبر نیست';
    setErrors(next);
    if (Object.keys(next).length) return;
    setLoading(true);
    setServerError('');
    try {
      await submitContactRequest({ ...form, mobile: toEnglishDigits(form.mobile.trim()), position: position.title });
      setDone(true);
    } catch (err) {
      setServerError(toApiError(err).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={done ? undefined : `ارسال رزومه — ${position.title}`} dismissible={!loading}>
      {done ? (
        <div role="status" className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-success-light text-success"><Icon name="check" size={28} /></span>
          <h2 className="text-lg font-bold">رزومه شما ارسال شد</h2>
          <p className="text-sm text-ink-2">برای موقعیت «{position.title}» با شما تماس گرفته خواهد شد.</p>
          <Button variant="outline" onClick={onClose}>بستن</Button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {serverError && <p role="alert" className="rounded-md bg-danger-light p-3 text-sm font-medium text-danger">{serverError}</p>}
          <Input label="نام و نام خانوادگی" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={errors.name} />
          <Input label="شماره موبایل" inputMode="numeric" dir="ltr" className="text-start" maxLength={11} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: toEnglishDigits(e.target.value).replace(/\D/g, '') })} error={errors.mobile} />
          <Input label="لینک رزومه (اختیاری)" placeholder="مثال: لینک LinkedIn یا فایل آنلاین" dir="ltr" className="text-start" value={form.resumeLink} onChange={(e) => setForm({ ...form, resumeLink: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>انصراف</Button>
            <Button type="submit" loading={loading}>ارسال</Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
